import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { getPatientById } from "@/data/mockData";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Droplets,
  AlertTriangle,
  Heart,
  Edit,
  Shield
} from "lucide-react";

const CURRENT_PATIENT_ID = "1";

export const PatientProfilePage = () => {
  const patient = getPatientById(CURRENT_PATIENT_ID);

  if (!patient) return null;

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Il Mio Profilo</h1>
          <p className="text-muted-foreground">Gestisci le tue informazioni personali</p>
        </div>
        <Button variant="outline" className="gap-2">
          <Edit className="h-4 w-4" />
          Modifica
        </Button>
      </div>

      {/* Profile Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <Avatar className="h-24 w-24 border-4 border-primary/20">
              <AvatarFallback className="bg-primary/10 text-primary text-3xl font-semibold">
                {patient.name.split(" ").map(n => n[0]).join("")}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl font-bold">{patient.name}</h2>
              <p className="text-muted-foreground font-mono">{patient.fiscalCode}</p>
              
              <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-4">
                <Badge variant="outline" className="gap-1">
                  <Calendar className="h-3 w-3" />
                  {patient.age} anni
                </Badge>
                <Badge variant="outline" className="gap-1">
                  <User className="h-3 w-3" />
                  {patient.gender === "M" ? "Maschio" : "Femmina"}
                </Badge>
                <Badge variant="outline" className="gap-1">
                  <Droplets className="h-3 w-3" />
                  Gruppo {patient.bloodType}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Informazioni di Contatto</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
              <Phone className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Telefono</p>
              <p className="font-medium">{patient.phone}</p>
            </div>
          </div>
          
          <Separator />
          
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
              <Mail className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{patient.email}</p>
            </div>
          </div>
          
          <Separator />
          
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
              <MapPin className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Indirizzo</p>
              <p className="font-medium">{patient.address}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Medical Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Informazioni Mediche</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Allergies */}
          <div>
            <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Allergie
            </p>
            {patient.allergies.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {patient.allergies.map((allergy, i) => (
                  <Badge key={i} variant="destructive">{allergy}</Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nessuna allergia nota</p>
            )}
          </div>
          
          <Separator />
          
          {/* Chronic Conditions */}
          <div>
            <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
              <Heart className="h-4 w-4" />
              Patologie Croniche
            </p>
            {patient.chronicConditions.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {patient.chronicConditions.map((condition, i) => (
                  <Badge key={i} variant="secondary">{condition}</Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nessuna patologia cronica</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Privacy */}
      <Card className="bg-muted/30">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Shield className="h-6 w-6 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-semibold">Privacy e Sicurezza</h3>
              <p className="text-sm text-muted-foreground mt-1">
                I tuoi dati sono protetti secondo le normative GDPR e sanitarie vigenti.
                Solo tu e il personale medico autorizzato possono accedere alle tue informazioni.
              </p>
              <Button variant="link" className="px-0 mt-2">Gestisci consensi</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientProfilePage;
